/* Bundle builder. Added by Oleg @ IAKOE.
   <ts-bundle-builder>: selection state, tier switching with memory, progress,
   slots, purchase options (real selling plans win over the stub preview), and the
   Smartrr cart contract POST (ONE dummy line item; picked products ride as
   line-item properties + the _smartrr_info stringified JSON - see
   workspace/plans/BUNDLE_BUILDER_PLAN.md).

   Selection model: `units` = array of picked variant ids in ADD ORDER. The
   effective pick = the first `tierCount` units, so switching 12 -> 6 shows and
   posts only the first 6 while the tail is stashed; switching back restores it.
   Any +/- while clamped merges the visible state and drops the stash (Oleg's
   rule: the tail survives only if the kept 6 stayed untouched). */

if (!customElements.get('ts-bundle-builder')) {
  customElements.define(
    'ts-bundle-builder',
    class TsBundleBuilder extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        try {
          this.config = JSON.parse(this.querySelector('[data-bb-config]').textContent);
        } catch (e) {
          console.error('ts-bundle-builder: bad config', e);
          return;
        }
        if (this.config.stub && Array.isArray(this.config.stub.frequencies)) {
          this.config.stub.frequencies = this.config.stub.frequencies.map((f) => f.trim()).filter(Boolean);
        }

        this.money = new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'GBP',
        });

        this.units = []; // picked product variant ids, in add order
        this.stash = []; // overflow kept from a higher tier
        this.purchase = 'onetime';

        this.tierInputs = Array.from(this.querySelectorAll('[data-bb-tier]'));
        this.items = Array.from(this.querySelectorAll('[data-bb-item]'));
        this.slotsWrap = this.querySelector('[data-bb-slots]');
        if (this.hasAttribute('data-slot-shake')) {
          this.slotsWrap?.addEventListener('click', (e) => {
            const cell = e.target.closest('.ts-bb__slot');
            if (cell && !cell.classList.contains('is-filled')) this.shakeQty();
          });
        }
        this.counterEl = this.querySelector('[data-bb-counter]');
        this.hintEl = this.querySelector('[data-bb-hint]');
        this.barEl = this.querySelector('[data-bb-bar]');
        this.atc = this.querySelector('[data-bb-atc]');
        this.atcBar = this.querySelector('[data-bb-atc-bar]');
        this.atcFixed = this.querySelector('[data-bb-atc-fixed]');
        this.atcFixed?.addEventListener('click', () => this.addToCart());
        this.freqSelect = this.querySelector('[data-bb-frequency]');
        this.subBody = this.querySelector('[data-bb-sub-body]');

        const checked = this.tierInputs.find((i) => i.checked) || this.tierInputs[0];
        this.tierId = checked ? checked.value : null;
        if (!this.tierId) return;

        // the button is server-rendered [disabled]; JS drives it via aria-disabled
        // so a click on the "inactive" button can shake the empty slots
        this.atc.disabled = false;
        this.atc.setAttribute('aria-disabled', 'true');

        this.tierInputs.forEach((input) => {
          input.addEventListener('change', () => this.switchTier(input.value));
        });

        this.items.forEach((item) => {
          const id = item.dataset.variantId;
          item.querySelector('[data-bb-plus]')?.addEventListener('click', () => this.add(id));
          item.querySelector('[data-bb-minus]')?.addEventListener('click', () => this.remove(id));
          if (this.hasAttribute('data-click-add') && !item.dataset.soldout) {
            item.querySelector('.ts-bb__item-media')?.addEventListener('click', () => this.add(id));
          }
        });

        this.querySelectorAll('[data-bb-purchase]').forEach((input) => {
          input.addEventListener('change', () => {
            this.purchase = input.value;
            this.render();
          });
        });

        this.freqSelect?.addEventListener('change', () => this.render());

        this.atc.addEventListener('click', () => this.addToCart());

        this.initSheet();
        this.render();
      }

      /* ---- mobile bottom sheet ---- */
      initSheet() {
        this.panel = this.querySelector('[data-bb-panel]');
        this.panelBar = this.querySelector('[data-bb-panel-toggle]');
        if (!this.panel || !this.panelBar) return;

        this.mq = matchMedia('(max-width: 1023px)');
        this.hasBar = this.hasAttribute('data-sticky-panel');
        const apply = () => {
          if (this.mq.matches) {
            this.classList.add('ts-bb--sheet');
            this.panelBar.hidden = !this.hasBar;
          } else {
            this.classList.remove('ts-bb--sheet');
            this.panelBar.hidden = true;
            this.closeSheet();
          }
          this.syncBar();
        };
        this.mq.addEventListener('change', apply);
        apply();

        this.panelBar.addEventListener('click', () => {
          // a tap mid-fold reopens instead of being swallowed
          if (this.classList.contains('is-closing')) {
            this.openSheet();
            return;
          }
          if (this.classList.contains('is-open')) this.closeSheet();
          else this.openSheet();
        });

        // The panel itself lives IN FLOW below the product list (a static
        // block); the fixed bottom bar is a shortcut to it. Once the real panel
        // scrolls onto the screen the bar hides (it would duplicate what is
        // already visible). Tap = expand as a bottom sheet; NOTHING opens on
        // scroll (Oleg). The same setting keeps the close gestures.
        if (this.hasBar) {
          this.io = new IntersectionObserver((entries) => {
            const e = entries[0];
            this.panelInView = e.isIntersecting;
            // fully scrolled past the builder (footer etc): no fixed bars at all
            this.panelPassed = !e.isIntersecting && e.boundingClientRect.bottom < 0;
            this.syncBar();
          });
          this.io.observe(this.panel);
          // the sticky ATC only bridges the gap UNTIL the real button is seen
          this.ioAtc = new IntersectionObserver((entries) => {
            const e = entries[0];
            this.atcInView = e.isIntersecting;
            this.atcAbove = !e.isIntersecting && e.boundingClientRect.bottom < 0;
            this.syncBar();
          });
          if (this.atc) this.ioAtc.observe(this.atc);

          const body = this.panel.querySelector('.ts-bb__panel-body');
          let touchY = null;
          body.addEventListener('touchstart', (e) => { touchY = e.touches[0].clientY; }, { passive: true });
          body.addEventListener('touchmove', (e) => {
            if (touchY === null || !this.classList.contains('is-open')) return;
            const d = e.touches[0].clientY - touchY;
            if (d > 40 && body.scrollTop <= 0) { touchY = null; this.closeSheet(); }
          }, { passive: true });
          body.addEventListener('wheel', (e) => {
            if (this.classList.contains('is-open') && body.scrollTop <= 0 && e.deltaY < -20) this.closeSheet();
          }, { passive: true });
        }
      }

      openSheet() {
        if (!this.mq || !this.mq.matches) return;
        this._closing = false;
        this.classList.remove('is-closing');
        this.classList.add('is-open');
        this.panelBar.setAttribute('aria-expanded', 'true');
        document.documentElement.style.overflow = 'hidden';
        this.syncBar();
      }

      closeSheet() {
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        const animate = this.hasAttribute('data-sheet-animate') && !reduce
          && this.classList.contains('is-open') && this.mq && this.mq.matches;
        if (!animate) {
          this.finishClose();
          return;
        }
        if (this._closing) return;
        this._closing = true;
        this.classList.add('is-closing');
        const body = this.panel.querySelector('.ts-bb__panel-body');
        const done = () => {
          if (!this._closing) return;
          this._closing = false;
          clearTimeout(timer);
          this.classList.remove('is-closing');
          this.finishClose();
        };
        // safety timer: the pane (and any browser killing the animation) must
        // never leave the sheet stuck half-closed
        const timer = setTimeout(done, 400);
        body.addEventListener('animationend', done, { once: true });
      }

      finishClose() {
        this.classList.remove('is-open');
        this.panelBar?.setAttribute('aria-expanded', 'false');
        document.documentElement.style.overflow = '';
        this.syncBar();
      }

      /* bar visibility + the body air under it (only the visible builder with a
         SHOWN bar owns document.body.paddingBottom). While the in-flow panel is
         on screen the bundle bar yields to the sticky ADD TO CART (Oleg). */
      syncBar() {
        if (!this.panelBar || !this.mq) return;
        const active = this.mq.matches && this.hasBar && !this.classList.contains('is-open');
        // bar: while the shopper is ABOVE the panel; sticky ATC: while the panel
        // is on screen but the real button is still ahead; past the builder
        // (footer) - nothing fixed at all (Oleg)
        const barShown = active && !this.panelInView && !this.panelPassed;
        const atcShown = active && !!this.panelInView && !this.atcInView && !this.atcAbove;
        this.panelBar.classList.toggle('is-away', active && !barShown);
        if (this.atcBar) this.atcBar.hidden = !atcShown || this.hidden;
        if (this.hidden) return;
        if (barShown) document.body.style.paddingBottom = (this.panelBar.offsetHeight || 66) + 'px';
        else if (atcShown) document.body.style.paddingBottom = (this.atcBar?.offsetHeight || 70) + 'px';
        else document.body.style.paddingBottom = '';
      }

      get tier() {
        return this.config.tiers[this.tierId] || { count: 6, price: 0, compare: 0, plans: [] };
      }

      /* first N units = what the customer actually gets at this tier */
      effectiveUnits() {
        return this.units.slice(0, this.tier.count);
      }

      qtyOf(id) {
        return this.effectiveUnits().filter((u) => u === id).length;
      }

      complete() {
        return this.effectiveUnits().length === this.tier.count;
      }

      add(id) {
        if (this.units.length >= this.tier.count) {
          // full: no empty slots, so the progress row + the buy button shake
          this.shakeEmptySlots();
          const atc = this.querySelector('[data-bb-atc]');
          if (atc) {
            atc.classList.remove('is-shaking');
            void atc.offsetWidth;
            atc.classList.add('is-shaking');
            atc.addEventListener('animationend', () => atc.classList.remove('is-shaking'), { once: true });
          }
          return;
        }
        const wasComplete = this.complete();
        this.units.push(id);
        this.stash = []; // edited: the overflow memory is void
        this.render();
        if (this.hasAttribute('data-atc-pulse') && !wasComplete && this.complete()) {
          [this.atc, this.atcFixed].forEach((btn) => {
            if (!btn) return;
            btn.classList.remove('is-pulse');
            void btn.offsetWidth;
            btn.classList.add('is-pulse');
            btn.addEventListener('animationend', () => btn.classList.remove('is-pulse'), { once: true });
          });
        }
        if (this.hasAttribute('data-counter-pulse') && this.counterEl) {
          this.counterEl.classList.remove('is-pulse');
          void this.counterEl.offsetWidth;
          this.counterEl.classList.add('is-pulse');
          this.counterEl.addEventListener('animationend', () => this.counterEl.classList.remove('is-pulse'), { once: true });
        }
      }

      remove(id) {
        const idx = this.units.lastIndexOf(id);
        if (idx === -1) return;
        this.units.splice(idx, 1);
        this.stash = [];
        this.render();
      }

      removeUnitAt(index) {
        if (index < 0 || index >= this.units.length) return;
        this.units.splice(index, 1);
        this.stash = [];
        this.render();
      }

      shakeQty() {
        // empty slot tapped: point at the steppers (close the sheet first so
        // they are actually on screen on mobile, then scroll to where you fill)
        if (this.classList.contains('is-open')) this.closeSheet();
        if (this.hasAttribute('data-slot-fill-scroll') && this.mq && this.mq.matches && this.items[0]) {
          const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.items[0].scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        }
        this.querySelectorAll('.ts-bb__item .ts-bb__stepper').forEach((st) => {
          st.classList.remove('is-shaking');
          void st.offsetWidth;
          st.classList.add('is-shaking');
          st.addEventListener('animationend', () => st.classList.remove('is-shaking'), { once: true });
        });
      }

      scrollToItem(id) {
        const byId = {};
        this.items.forEach((item) => (byId[item.dataset.variantId] = item));
        const target = byId[id];
        if (!target) return;
        if (this.classList.contains('is-open')) this.closeSheet();
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      }

      switchTier(newId) {
        const prev = this.tier.count;
        this.tierId = newId;
        const next = this.tier.count;
        if (next < this.units.length) {
          // going down: stash the tail, keep showing/posting the first `next`
          this.stash = this.units.slice(next);
          this.units = this.units.slice(0, next);
        } else if (next > prev && this.stash.length) {
          // back up with the kept part untouched: restore
          this.units = this.units.concat(this.stash).slice(0, next);
          this.stash = [];
        }
        this.querySelectorAll('.ts-bb__tier').forEach((label) => {
          const input = label.querySelector('[data-bb-tier]');
          label.classList.toggle('is-selected', input && input.checked);
        });
        this.rebuildSlots();
        this.render();
      }

      fmt(cents) {
        return this.money.format(cents / 100);
      }

      /* the active subscription offer: real plans win, stub only fills the void */
      subOffer() {
        const plans = this.tier.plans || [];
        if (plans.length) {
          const chosen = this.freqSelect && plans.find((p) => String(p.id) === this.freqSelect.value);
          const plan = chosen || plans[0];
          return { real: true, plan, price: plan.price, compare: plan.compare || this.tier.compare };
        }
        if (this.config.stub) {
          const d = this.config.stub.discount || 0;
          return { real: false, plan: null, price: Math.round(this.tier.price * (1 - d / 100)), compare: this.tier.compare };
        }
        return null;
      }

      rebuildSlots() {
        if (!this.slotsWrap) return;
        const count = this.tier.count;
        this.slotsWrap.dataset.count = count;
        const cells = Array.from(this.slotsWrap.children);
        while (cells.length > count) cells.pop().remove();
        while (cells.length < count) {
          const clone = this.slotsWrap.children[0].cloneNode(true);
          clone.querySelector('img.ts-bb__slot-fill')?.remove();
          this.slotsWrap.append(clone);
          cells.push(clone);
        }
      }

      renderSlots() {
        if (!this.slotsWrap) return;
        const units = this.effectiveUnits();
        const byId = {};
        this.items.forEach((item) => (byId[item.dataset.variantId] = item));
        const canScroll = this.hasAttribute('data-slot-scroll');
        const canRemove = this.hasAttribute('data-slot-remove');
        Array.from(this.slotsWrap.children).forEach((cell, i) => {
          const unit = units[i];
          const icon = cell.querySelector('.ts-bb__slot-icon');
          let img = cell.querySelector('img.ts-bb__slot-fill');
          let cross = cell.querySelector('.ts-bb__slot-x');
          if (unit && byId[unit]) {
            const src = byId[unit].dataset.image;
            if (!img) {
              img = document.createElement('img');
              img.className = 'ts-bb__slot-fill';
              img.alt = '';
              cell.append(img);
            }
            if (img.getAttribute('src') !== src) img.src = src;
            if (icon) icon.hidden = true;
            cell.classList.add('is-filled');
            cell.dataset.unit = unit;
            cell.dataset.index = i;
            if (canScroll && !cell.dataset.wiredScroll) {
              cell.dataset.wiredScroll = '1';
              cell.addEventListener('click', (e) => {
                if (e.target.closest('.ts-bb__slot-x')) return;
                if (cell.classList.contains('is-filled') && cell.dataset.unit) this.scrollToItem(cell.dataset.unit);
              });
            }
            if (canRemove && !cross) {
              cross = document.createElement('button');
              cross.type = 'button';
              cross.className = 'ts-bb__slot-x';
              cross.setAttribute('aria-label', 'Remove');
              cross.innerHTML = '<svg width="10" height="10" viewBox="0 0 13 13" fill="none" aria-hidden="true"><path d="M0.527341 0.500003L11.5268 11.4994M11.4996 0.52713L0.500201 11.5266" stroke="currentColor" stroke-linecap="round"/></svg>';
              cross.addEventListener('click', () => this.removeUnitAt(Number(cell.dataset.index)));
              cell.append(cross);
            }
          } else {
            img?.remove();
            cross?.remove();
            if (icon) icon.hidden = false;
            cell.classList.remove('is-filled');
            delete cell.dataset.unit;
          }
        });
      }

      render() {
        const count = this.tier.count;
        const picked = this.effectiveUnits().length;

        if (this.counterEl) this.counterEl.textContent = picked + '/' + count;
        if (this.hintEl) {
          this.hintEl.textContent = this.complete()
            ? this.hintEl.dataset.complete
            : this.hintEl.dataset.addTpl.replace('[n]', count - picked);
        }
        if (this.barEl) this.barEl.style.width = (picked / count) * 100 + '%';

        // steppers
        const atCap = this.units.length >= count;
        this.classList.toggle('is-full', atCap);
        this.items.forEach((item) => {
          const id = item.dataset.variantId;
          const qty = this.qtyOf(id);
          const qtyEl = item.querySelector('[data-bb-qty]');
          if (qtyEl) qtyEl.textContent = qty;
          const plus = item.querySelector('[data-bb-plus]');
          const minus = item.querySelector('[data-bb-minus]');
          if (plus) {
            // real disabled only when sold out: a disabled button swallows the
            // click, and at cap we still want the "bundle complete" shake
            plus.disabled = !!item.dataset.soldout;
            plus.setAttribute('aria-disabled', atCap || item.dataset.soldout ? 'true' : 'false');
          }
          if (minus) minus.disabled = qty === 0;
        });

        this.renderSlots();

        // purchase option prices
        const setText = (sel, text, hide) => {
          const el = this.querySelector(sel);
          if (!el) return;
          el.textContent = text;
          if (hide !== undefined) el.hidden = hide;
        };
        setText('[data-bb-price-onetime]', this.fmt(this.tier.price));
        setText('[data-bb-compare-onetime]', this.fmt(this.tier.compare), !(this.tier.compare > this.tier.price));

        const offer = this.subOffer();
        if (offer) {
          setText('[data-bb-price-sub]', this.fmt(offer.price));
          setText('[data-bb-compare-sub]', this.fmt(offer.compare), !(offer.compare > offer.price));
          this.renderFrequencies(offer);
        }

        // sticky ATC price = the selected purchase option (the PDP button pattern)
        const barMode = this.purchase === 'subscribe' && offer ? offer : this.tier;
        setText('[data-bb-bar-price]', this.fmt(barMode.price));
        setText('[data-bb-bar-compare]', this.fmt(barMode.compare), !(barMode.compare > barMode.price));

        // option selected states + sub body visibility
        this.querySelectorAll('.ts-bb__option').forEach((label) => {
          const input = label.querySelector('[data-bb-purchase]');
          label.classList.toggle('is-selected', input && input.checked);
        });
        if (this.subBody) this.subBody.hidden = this.purchase !== 'subscribe';

        // ATC
        const ready = this.complete();
        this.atc.setAttribute('aria-disabled', ready ? 'false' : 'true');
        this.atc.classList.toggle('is-disabled', !ready);
        if (this.atcFixed) {
          this.atcFixed.setAttribute('aria-disabled', ready ? 'false' : 'true');
          this.atcFixed.classList.toggle('is-disabled', !ready);
        }
      }

      renderFrequencies(offer) {
        if (!this.freqSelect) return;
        const label = this.config.frequencyLabel || '';
        let opts;
        if (offer.real) {
          opts = (this.tier.plans || []).map((p) => ({ value: String(p.id), text: (label + ' ' + p.name).trim() }));
        } else {
          opts = (this.config.stub.frequencies || []).map((f) => ({ value: f, text: (label + ' ' + f).trim() }));
        }
        const current = this.freqSelect.value;
        const want = opts.map((o) => o.value).join('|');
        if (this.freqSelect.dataset.opts !== want) {
          this.freqSelect.dataset.opts = want;
          this.freqSelect.innerHTML = '';
          opts.forEach((o) => {
            const el = document.createElement('option');
            el.value = o.value;
            el.textContent = o.text;
            this.freqSelect.append(el);
          });
          if (opts.some((o) => o.value === current)) this.freqSelect.value = current;
        }
      }

      shakeEmptySlots() {
        const row = this.querySelector('.ts-bb__progress-row');
        if (row) {
          row.classList.remove('is-shaking');
          void row.offsetWidth;
          row.classList.add('is-shaking');
          row.addEventListener('animationend', () => row.classList.remove('is-shaking'), { once: true });
        }
        if (!this.slotsWrap) return;
        Array.from(this.slotsWrap.children).forEach((cell) => {
          if (cell.classList.contains('is-filled')) return;
          cell.classList.remove('is-shaking');
          // restart the animation even on repeated clicks
          void cell.offsetWidth;
          cell.classList.add('is-shaking');
          cell.addEventListener('animationend', () => cell.classList.remove('is-shaking'), { once: true });
        });
      }

      async addToCart() {
        if (!this.complete()) {
          this.shakeEmptySlots();
          return;
        }
        if (!window.iaCartAPI) return;

        const offer = this.purchase === 'subscribe' ? this.subOffer() : null;
        const sellingPlan = offer && offer.real && offer.plan ? offer.plan.id : null;

        // composition: variantId -> { qty, title }
        const byId = {};
        this.items.forEach((item) => (byId[item.dataset.variantId] = item));
        const counts = new Map();
        this.effectiveUnits().forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));

        const properties = { Bundle: this.dataset.bundleName };
        const infoItems = [];
        counts.forEach((quantity, id) => {
          const title = byId[id] ? byId[id].dataset.title : id;
          properties[title] = quantity;
          const entry = { id: Number(id), quantity };
          if (sellingPlan) entry.selling_plan = sellingPlan;
          infoItems.push(entry);
        });
        properties['_smartrr_info'] = JSON.stringify({
          items: infoItems,
          date: Date.now(),
          page: window.location.toString(),
          bundle: { name: this.dataset.bundleName, slug: this.dataset.bundleSlug },
        });

        const line = { id: Number(this.tierId), quantity: 1, properties };
        if (sellingPlan) line.selling_plan = sellingPlan;

        const buttons = [this.atc, this.atcFixed].filter(Boolean);
        buttons.forEach((btn) => btn.classList.add('adding'));
        try {
          const cartObj = await window.iaCartAPI.addToCartJson({ items: [line] });
          document.querySelector('ia-cart')?.open(cartObj);
        } catch (error) {
          console.error('ts-bundle-builder add to cart failed:', error);
        } finally {
          buttons.forEach((btn) => btn.classList.remove('adding'));
        }
      }
    }
  );
}

/* Bundle switcher: builds ts-pill buttons from the sibling <ts-bundle-builder>
   blocks' data attributes (a theme-block section cannot expose block settings to
   the section's Liquid - the documented gotcha) and toggles which bundle shows.
   Hidden while the section has fewer than two bundles, so a single-bundle page
   looks exactly like the pre-blocks build. */
if (!customElements.get('ts-bundle-switcher')) {
  customElements.define(
    'ts-bundle-switcher',
    class TsBundleSwitcher extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        // blocks render after the switcher in the section; defer one tick so the
        // editor's re-renders (element inserted before its siblings) also work
        setTimeout(() => this.build(), 0);
      }

      build() {
        this.innerHTML = '';
        const section = this.closest('.ts-bundle-builder-wrapper') || this.parentElement;
        this.builders = Array.from(section.querySelectorAll('ts-bundle-builder'));
        if (this.builders.length < 2) {
          this.hidden = true;
          return;
        }
        this.hidden = false;
        this.builders.forEach((bb, i) => {
          bb.hidden = i !== 0;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ts-pill ts-pill--bundle ia-font-body ia-uppercase';
          if (i === 0) btn.classList.add('is-active');
          if (bb.dataset.switchIcon) {
            const img = document.createElement('img');
            img.src = bb.dataset.switchIcon;
            img.alt = '';
            img.width = 24;
            img.height = 24;
            img.className = 'ts-pill__icon';
            btn.append(img);
          }
          btn.append(document.createTextNode(bb.dataset.switchLabel || ''));
          btn.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
          btn.addEventListener('click', () => this.select(i));
          this.append(btn);
        });
      }

      select(index) {
        this.builders.forEach((bb, i) => {
          bb.hidden = i !== index;
          if (i === index) bb.syncBar?.();
        });
        Array.from(this.children).forEach((btn, i) => {
          btn.classList.toggle('is-active', i === index);
          btn.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
      }
    }
  );
}
